/**
 * What each section is for, in the words of the person who runs the agency.
 *
 * Every screen in the rail is reachable before the work behind it is finished,
 * so each one has to explain itself. The rule for this file: describe what will
 * live on the page in terms of the reader's job — the clients they serve, the
 * shifts they have to fill, the licence that expires on Friday. Never in terms
 * of ours. An owner does not have a build plan; they have a Monday.
 *
 * So there is nothing here about slices, phases, releases or what is "coming
 * soon". A screen that describes the thing it will hold is useful the day it is
 * empty and still correct the day it fills up.
 *
 * Keyed by the same paths as `NAVIGATION`, and `page-copy.spec.ts` asserts
 * the two sets match exactly in both directions — an item cannot reach the rail
 * without copy, and copy cannot outlive the item it was written for.
 */
export interface PageCopy {
  /** What is missing, named as the thing itself. Never "No data". */
  title: string;
  /** What belongs here and why someone would come looking for it. */
  explanation: string;
}

export const PAGE_COPY: Record<string, PageCopy> = {
  // -- Care ----------------------------------------------------------------
  '/clients': {
    title: 'No clients yet',
    explanation:
      'Everyone your agency cares for lives here: their address and how to get in, who to call in an emergency, which payer covers them and how many hours that payer has approved. This is where a referral becomes someone you serve.',
  },
  '/care-plans': {
    title: 'No care plans yet',
    explanation:
      'What a caregiver is expected to do on each visit, and the goals those tasks add up to. The care plan is what turns an hour on the schedule into care you can show a surveyor.',
  },
  '/assessments': {
    title: 'No assessments yet',
    explanation:
      'The in-home visits where you work out what someone needs and how long it takes to do it. Completed assessments live here with the reassessments they fall due for.',
  },

  // -- Operations ----------------------------------------------------------
  '/schedule': {
    title: 'Nothing on the schedule',
    explanation:
      'The week laid out by caregiver and by client: who is covered, who is not, and which shifts are still looking for someone. Filling tomorrow starts on this screen.',
  },
  '/visits': {
    title: 'No visits yet',
    explanation:
      'Every visit after it has happened — clock in and clock out, what was done, and what was missed. Anything that will not stand up to billing or payroll is flagged here first.',
  },
  '/caregivers': {
    title: 'No caregivers yet',
    explanation:
      'Your field staff: what they are qualified to do, how far they will travel, which clients they already know, and the hours they have told you they can work.',
  },
  '/credentials': {
    title: 'No credentials on file',
    explanation:
      'Licences, certifications, background checks and health screenings, each with the date it runs out. This is the screen that tells you a caregiver cannot work Monday while it is still Thursday.',
  },

  // -- Revenue -------------------------------------------------------------
  '/billing': {
    title: 'Nothing ready to bill',
    explanation:
      'Finished visits priced against each payer, waiting to go out — and beside them the ones held back, with the reason they were held, so you can fix them rather than write them off.',
  },
  '/invoices': {
    title: 'No invoices yet',
    explanation:
      'What you have sent to payers and private-pay families, what has come back, and what is old enough that someone should be picking up the phone.',
  },
  '/payroll': {
    title: 'No pay periods yet',
    explanation:
      'Caregiver hours for each period — regular, overtime, travel and mileage — laid out for you to check before the money leaves. Disputes are settled against the visits they came from.',
  },
  '/rates': {
    title: 'No rates set',
    explanation:
      'What each payer pays you for a service, and what you pay a caregiver to deliver it. Every invoice you raise and every hour you pay is priced from this table, so it is worth getting right once.',
  },

  // -- Admin ---------------------------------------------------------------
  '/admin/people': {
    title: 'No one else here yet',
    explanation:
      'The office staff who can sign in to this console. Invite someone, decide what they are allowed to open, and switch their access off the day they leave — not the week after.',
  },
  '/admin/roles': {
    title: 'No roles of your own yet',
    explanation:
      'A role is a named bundle of what a person can open, read and change. Your agency starts with a standard set — scheduler, care manager, biller and the rest — and you can copy any of them when your office divides the work differently.',
  },
  '/admin/settings': {
    title: 'Nothing set up yet',
    explanation:
      'Your legal and trading names, your NPI and Medicaid provider ID, the states you are enrolled in, and the time zone your schedule is read in. The rest of the console follows what you put here.',
  },
  '/admin/credential-types': {
    title: 'No credential types yet',
    explanation:
      'What your agency requires of a caregiver before they can work — a TB test, a background check, a licence — and which of those stop a shift rather than just being chased. Your state sets most of it; this is where you say what applies here.',
  },
  '/admin/audit': {
    title: 'Nothing to show yet',
    explanation:
      'Every sign-in, every client record opened and every change made, with the person who did it and the minute they did it. When a payer or a surveyor asks who saw a file, this is the answer.',
  },
};

/** The copy for a section, if there is any. */
export function copyForPath(path: string): PageCopy | undefined {
  return PAGE_COPY[path];
}
